import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { toggleTheme } from '../../store/slices/themeSlice';
import { useAppDispatch } from '../../store/hooks';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CText, CLoader } from '../../components/common';
import { FiBell, FiSun, FiMoon, FiCheckCircle } from 'react-icons/fi';
import { IoStar } from 'react-icons/io5';
import { handleExpertSearch, Expert } from '../../api';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const isDark = useAppSelector((state) => state.theme.isDark);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [experts, setExperts] = useState<Expert[]>([]);

  useEffect(() => {
    loadExperts();
  }, []);

  const loadExperts = async () => {
    setLoading(true);
    try {
      const response = await handleExpertSearch('', 1);
      setExperts(response.experts.slice(0, 6)); // Show top 6 experts
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setLoading(false);
    }
  };

  const ExpertCard = ({ expert }: { expert: Expert }) => (
    <div
      className="rounded-xl p-4 cursor-pointer transition-opacity hover:opacity-90"
      style={{ backgroundColor: colors.inputBackground }}
      onClick={() => navigate(`/expert/${expert.user_id}`)}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
            style={{ backgroundColor: colors.primary, color: colors.white }}
          >
            {expert.name.charAt(0)}
          </div>
          {expert.user_avg_rating > 0 ? (
            <div className="flex items-center gap-1 mt-1">
              {React.createElement(IoStar as any, { size: 12, color: '#FFD700' })}
              <CText type="r12" color={colors.grayScale500}>
                {expert.user_avg_rating.toFixed(1)}
              </CText>
            </div>
          ) : (
            <CText type="r10" color={colors.grayScale500} className="mt-1">
              No Rating
            </CText>
          )}
        </div>

        {/* Details */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <CText type="B16" color={colors.textColor}>
              {expert.name}
            </CText>
            {expert.is_verified && (
              React.createElement(FiCheckCircle as any, { size: 16, color: colors.primary })
            )}
          </div>
          <CText type="r14" color={colors.grayScale500}>
            {expert.sebi_qualification}
          </CText>
          <CText type="r12" color={colors.grayScale400} className="mt-1">
            {expert.bio.length > 60 ? expert.bio.slice(0, 60) + '...' : expert.bio}
          </CText>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-5xl lg:mx-auto">
      {loading && <CLoader />}

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <CText type="r14" color={colors.grayScale500}>{strings.home}</CText>
          <CText type="B18" color={colors.textColor}>
            Book Calls with SEBI Registered Experts
          </CText>
        </div>
        <div className="flex gap-2">
          <button
            className="bg-transparent border-none cursor-pointer p-2 transition-colors hover:opacity-70"
            style={{ color: colors.iconColor }}
            onClick={() => dispatch(toggleTheme())}
          >
            {React.createElement(isDark ? FiSun : FiMoon as any, { size: 22 })}
          </button>
          <button
            className="bg-transparent border-none cursor-pointer p-2 transition-colors hover:opacity-70"
            style={{ color: colors.iconColor }}
            onClick={() => navigate(routes.notification)}
          >
            {React.createElement(FiBell as any, { size: 22 })}
          </button>
        </div>
      </div>

      {/* Expert Advisors Section */}
      <div className="mt-6">
        <CText type="M12" color={colors.primary} style={{ marginBottom: '12px' }}>
          Top Expert Advisors
        </CText>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {experts.map((expert) => (
            <ExpertCard key={expert.user_id} expert={expert} />
          ))}
        </div>

        {experts.length > 0 && (
          <div
            className="text-right cursor-pointer mt-4"
            onClick={() => navigate('/experts')}
          >
            <CText
              type="M12"
              color={colors.primary}
              style={{ textDecoration: 'underline' }}
            >
              View All
            </CText>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Home;
